import { useState, useCallback } from 'react';

let _tempCounter = 0;
const nextTempId = () => `pending-${Date.now()}-${++_tempCounter}`;

/**
 * Staged submittals manager for Division 7 wizards.
 * Upload/delete changes stay local until commit (Save/Draft).
 * Cancel/close triggers rollback — backend untouched.
 */
export function useSubmittalsManager() {
  const [committed, setCommitted] = useState([]);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [pendingDeletes, setPendingDeletes] = useState(new Set());

  const init = useCallback((submittals) => {
    setCommitted(submittals || []);
    setPendingUploads([]);
    setPendingDeletes(new Set());
  }, []);

  const addUpload = useCallback((file, docType) => {
    setPendingUploads(prev => [...prev, {
      tempId: nextTempId(),
      file,
      docType,
      fileName: file.name,
      bytes: file.size,
    }]);
  }, []);

  const removeItem = useCallback((id) => {
    if (typeof id === 'string' && id.startsWith('pending-')) {
      setPendingUploads(prev => prev.filter(p => p.tempId !== id));
    } else {
      setPendingDeletes(prev => new Set([...prev, id]));
    }
  }, []);

  const undoDelete = useCallback((id) => {
    setPendingDeletes(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const rollback = useCallback(() => {
    setPendingUploads([]);
    setPendingDeletes(new Set());
  }, []);

  const displayList = [
    ...committed.map(s => ({
      ...s,
      _status: pendingDeletes.has(s.id) ? 'pendingDelete' : 'committed',
    })),
    ...pendingUploads.map(p => ({
      id: p.tempId,
      documentType: p.docType,
      fileName: p.fileName,
      bytes: p.bytes,
      _status: 'pending',
      _file: p.file,
    })),
  ];

  const isDirty = pendingUploads.length > 0 || pendingDeletes.size > 0;

  const commitCount = () => ({
    uploads: pendingUploads.length,
    deletes: pendingDeletes.size,
  });

  return {
    committed,
    displayList,
    pendingUploads,
    pendingDeletes,
    isDirty,
    init,
    addUpload,
    removeItem,
    undoDelete,
    rollback,
    commitCount,
  };
}

/**
 * Commits staged submittals to backend.
 * Call after product create/update succeeds.
 * @param {Object} manager - return value of useSubmittalsManager
 * @param {string} productId
 * @param {string} projectId
 * @param {string} apiPrefix - e.g. 'roofing-products'
 * @param {Function} axiosInstance - axios
 * @param {string} API - base API url
 * @returns {Promise<void>}
 */
export async function commitSubmittals(manager, productId, projectId, apiPrefix, axiosInstance, API) {
  const { pendingUploads, pendingDeletes } = manager;

  for (const pending of pendingUploads) {
    const fd = new FormData();
    fd.append('file', pending.file);
    fd.append('document_type', pending.docType);
    await axiosInstance.post(
      `${API}/${apiPrefix}/${projectId}/${productId}/upload-submittal`,
      fd
    );
  }

  for (const id of pendingDeletes) {
    await axiosInstance.delete(
      `${API}/${apiPrefix}/${projectId}/${productId}/submittals/${id}`
    );
  }
}
